import React, { useState, useEffect } from 'react'
import { getSessionHeaders } from '../../lib/serverSession';
import { logAction } from '../../lib/personalActivityLogger'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface User {
  id: number
  username: string
  full_name: string | null
  email: string
  commission_balances?: {
    usdt_balance: number
    share_balance: number
    total_earned_usdt: number
    total_earned_shares: number
    total_withdrawn_usdt: number
  }
  payment_transactions?: Array<{
    id: string
    amount: number
    currency: string
    network: string
    status: string
    created_at: string
  }>
}

interface FinancialActionsModalProps {
  isOpen: boolean
  onClose: () => void
  user: User
  adminUser?: any
  onUpdate: () => void
}

export const FinancialActionsModal: React.FC<FinancialActionsModalProps> = ({
  isOpen,
  onClose,
  user,
  onUpdate
}) => {
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'commission' | 'shares'>('commission')

  // Commission adjustment states
  const [usdtAdjustment, setUsdtAdjustment] = useState('')
  const [shareAdjustment, setShareAdjustment] = useState('')
  const [adjustmentReason, setAdjustmentReason] = useState('')
  // v4.7.63 — TOTP step-up required server-side; collected here
  const [totpToken, setTotpToken] = useState('')

  // v4.7.53 — Personal Activity: record which user is being financially adjusted
  useEffect(() => {
    if (!isOpen || !user?.id) return
    logAction('user_edit', 'open_financial_actions', {
      target_type: 'user',
      target_id: String(user.id),
      target_label: user.username || user.full_name || user.email || `User ${user.id}`
    })
  }, [isOpen, user?.id])

  if (!isOpen) return null

  const handleCommissionAdjustment = async () => {
    if (!usdtAdjustment && !shareAdjustment) {
      alert('Please enter an adjustment amount')
      return
    }

    if (!adjustmentReason.trim() || adjustmentReason.trim().length < 10) {
      alert('Please provide a reason (at least 10 characters) for the adjustment')
      return
    }

    const cleanTotp = totpToken.replace(/\s+/g, '')
    if (!/^\d{6}$/.test(cleanTotp) && !/^\d{8}$/.test(cleanTotp)) {
      alert('Enter your 6-digit authenticator code (or 8-digit backup code) to authorize this adjustment.')
      return
    }

    setLoading(true)
    try {
      // Update commission balances via API
      const response = await fetch(`${API_BASE_URL}/api/admin/financial-actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'adjust_commission',
          data: {
            userId: user.id,
            usdtAdjustment,
            shareAdjustment,
            reason: adjustmentReason,
            totpToken: cleanTotp
          }
        })
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      alert('Commission balance adjusted successfully')
      setUsdtAdjustment('')
      setShareAdjustment('')
      setAdjustmentReason('')
      setTotpToken('')
      onUpdate()
    } catch (err: any) {
      console.error('Error adjusting commission:', err)
      alert('Failed to adjust commission: ' + err.message)
    } finally {
      setLoading(false)
    }
  }



  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-white">
              ⚙️ Financial Management - {user.full_name || user.username}
            </h2>
            <p className="text-white mt-1">@{user.username} (ID: {user.id})</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('commission')}
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'commission'
                ? 'text-yellow-400 border-b-2 border-yellow-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            💰 Commission Management
          </button>
          <button
            onClick={() => setActiveTab('shares')}
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'shares'
                ? 'text-yellow-400 border-b-2 border-yellow-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            📈 Share Management
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Commission Management Tab */}
          {activeTab === 'commission' && (
            <div className="space-y-6">
              <div className="glass-card p-4">
                <h3 className="text-lg font-semibold text-white mb-4">Current Commission Balances</h3>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="text-center">
                    <p className="text-sm text-white">USDT Balance</p>
                    <p className="text-2xl font-bold text-blue-400">
                      ${(user.commission_balances?.usdt_balance || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-white">Share Balance</p>
                    <p className="text-2xl font-bold text-yellow-400">
                      {(user.commission_balances?.share_balance || 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                <h4 className="text-md font-semibold text-white mb-4">Adjust Balances</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      USDT Adjustment (+ or -)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={usdtAdjustment}
                      onChange={(e) => setUsdtAdjustment(e.target.value)}
                      placeholder="e.g., +100 or -50"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Share Adjustment (+ or -)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={shareAdjustment}
                      onChange={(e) => setShareAdjustment(e.target.value)}
                      placeholder="e.g., +10 or -5"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Reason for Adjustment * (min 10 chars)
                  </label>
                  <textarea
                    value={adjustmentReason}
                    onChange={(e) => setAdjustmentReason(e.target.value)}
                    placeholder="Explain why this adjustment is being made..."
                    rows={3}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Authenticator Code * (6-digit TOTP or 8-digit backup)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={totpToken}
                    onChange={(e) => setTotpToken(e.target.value)}
                    placeholder="123 456"
                    maxLength={9}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white tracking-widest font-mono focus:outline-none focus:border-yellow-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Per-call cap: $1000 / 100 shares. Authorized admin only.</p>
                </div>
                <button
                  onClick={handleCommissionAdjustment}
                  disabled={loading}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded-lg disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Apply Commission Adjustment'}
                </button>
              </div>
            </div>
          )}

          {/* Share Management Tab */}
          {activeTab === 'shares' && (
            <div className="space-y-6">
              <div className="glass-card p-4">
                <h3 className="text-lg font-semibold text-white mb-4">Share Management</h3>
                <p className="text-white text-center py-8">
                  Share management features coming soon...
                  <br />
                  This will include manual share allocation and shareholding status management.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
